import { type Dtos } from "@inrev/common";
import { type ProCon, type ProConRelationship, relationshipVerb } from "@portal/api/decisioning";
import { cn } from "../../../../lib/utils";
import { Text } from "../../../../primitives";

type RelationshipType = Dtos.Decisioning.ProConRelationshipType;

type ToneClasses = { text: string; underline: string; background: string };

export const RELATIONSHIP_TONES = {
	success: {
		text: "text-intent-success-text",
		underline: "decoration-intent-success-border",
		background: "bg-intent-success-bg",
	},
	danger: {
		text: "text-intent-danger-text",
		underline: "decoration-intent-danger-border",
		background: "bg-intent-danger-bg",
	},
	info: {
		text: "text-intent-info-text",
		underline: "decoration-intent-info-border",
		background: "bg-intent-info-bg",
	},
} satisfies Record<string, ToneClasses>;

/** Compounds pairs the same kind, so it reads green between pros and red between cons. */
export const relationshipTone = (
	type: RelationshipType,
	kind: Dtos.Decisioning.ProConKind,
): ToneClasses => {
	switch (type) {
		case "mitigates":
			return RELATIONSHIP_TONES.success;
		case "compounds":
			return kind === "pro" ? RELATIONSHIP_TONES.success : RELATIONSHIP_TONES.danger;
		case "explains":
			return RELATIONSHIP_TONES.info;
	}
};

export type RelationLinkProps = {
	relationship: ProConRelationship;
	/** The card this link sits on. */
	viewerId: ProCon["id"];
	other: ProCon;
	onHoverChange: (otherId: ProCon["id"] | null) => void;
	onJump: (otherId: ProCon["id"]) => void;
};

/** `→ mitigates Name` on the card the relationship was added from, `← mitigated by Name` on the other. */
export const RelationLink = ({
	relationship,
	viewerId,
	other,
	onHoverChange,
	onJump,
}: RelationLinkProps) => {
	const tone = relationshipTone(relationship.type, other.kind);
	const outgoing = relationship.sourceProConId === viewerId;
	return (
		<button
			type="button"
			data-testid={`relation-link-${relationship.id}`}
			data-direction={outgoing ? "out" : "in"}
			className={cn(
				"inline-flex max-w-full cursor-pointer items-baseline gap-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-bc-focus",
				tone.text,
			)}
			onMouseEnter={() => onHoverChange(other.id)}
			onMouseLeave={() => onHoverChange(null)}
			onFocus={() => onHoverChange(other.id)}
			onBlur={() => onHoverChange(null)}
			onClick={() => onJump(other.id)}
		>
			<Text type="caption" className="shrink-0 text-inherit">
				{outgoing ? "→" : "←"} {relationshipVerb(relationship, viewerId)}
			</Text>
			<Text
				type="caption"
				className={cn(
					"min-w-0 font-semibold text-inherit underline decoration-2 underline-offset-2",
					tone.underline,
				)}
				truncate
			>
				{other.factor.name}
			</Text>
		</button>
	);
};
